/**
 * DiagramSpec validator — catches DSL errors before rendering.
 * Usable in both browser and CLI (vitest).
 */

import type { DiagramSpec } from "./types"

export interface ValidationError {
  path: string
  message: string
  severity: "error" | "warning"
}

export function validateSpec(spec: unknown): ValidationError[] {
  const errors: ValidationError[] = []

  if (!spec || typeof spec !== "object") {
    errors.push({ path: "", message: "Spec must be an object", severity: "error" })
    return errors
  }

  const s = spec as Record<string, unknown>

  // Required top-level fields
  if (!s.id || typeof s.id !== "string") {
    errors.push({ path: "id", message: "Missing or invalid id (must be string)", severity: "error" })
  }
  if (!s.name || typeof s.name !== "string") {
    errors.push({ path: "name", message: "Missing or invalid name (must be string)", severity: "error" })
  }
  if (!Array.isArray(s.layers)) {
    errors.push({ path: "layers", message: "Missing or invalid layers (must be array)", severity: "error" })
    return errors
  }
  if (!Array.isArray(s.nodes)) {
    errors.push({ path: "nodes", message: "Missing or invalid nodes (must be array)", severity: "error" })
    return errors
  }
  if (!Array.isArray(s.edges)) {
    errors.push({ path: "edges", message: "Missing or invalid edges (must be array)", severity: "error" })
    return errors
  }
  if (!Array.isArray(s.views)) {
    errors.push({ path: "views", message: "Missing or invalid views (must be array)", severity: "error" })
    return errors
  }

  const d = spec as DiagramSpec

  // Duplicate IDs
  const layerIds = new Set<string>()
  d.layers.forEach((l, i) => {
    if (!l.id) errors.push({ path: `layers[${i}].id`, message: "Missing layer id", severity: "error" })
    if (!l.name) errors.push({ path: `layers[${i}].name`, message: "Missing layer name", severity: "error" })
    if (!l.color) errors.push({ path: `layers[${i}].color`, message: "Missing layer color", severity: "error" })
    if (layerIds.has(l.id))
      errors.push({ path: `layers[${i}].id`, message: `Duplicate layer id: "${l.id}"`, severity: "error" })
    layerIds.add(l.id)
  })

  const nodeIds = new Set<string>()
  d.nodes.forEach((n, i) => {
    if (!n.id) errors.push({ path: `nodes[${i}].id`, message: "Missing node id", severity: "error" })
    if (!n.label) errors.push({ path: `nodes[${i}].label`, message: "Missing node label", severity: "error" })
    if (!n.layer) {
      errors.push({ path: `nodes[${i}].layer`, message: "Missing node layer", severity: "error" })
    } else if (!layerIds.has(n.layer)) {
      errors.push({
        path: `nodes[${i}].layer`,
        message: `Node references unknown layer: "${n.layer}"`,
        severity: "error",
      })
    }
    if (nodeIds.has(n.id))
      errors.push({ path: `nodes[${i}].id`, message: `Duplicate node id: "${n.id}"`, severity: "error" })
    nodeIds.add(n.id)
  })

  const edgeIds = new Set<string>()
  d.edges.forEach((e, i) => {
    if (!e.id) errors.push({ path: `edges[${i}].id`, message: "Missing edge id", severity: "error" })
    if (!e.source) {
      errors.push({ path: `edges[${i}].source`, message: "Missing edge source", severity: "error" })
    } else if (!nodeIds.has(e.source)) {
      errors.push({
        path: `edges[${i}].source`,
        message: `Edge references unknown source node: "${e.source}"`,
        severity: "error",
      })
    }
    if (!e.target) {
      errors.push({ path: `edges[${i}].target`, message: "Missing edge target", severity: "error" })
    } else if (!nodeIds.has(e.target)) {
      errors.push({
        path: `edges[${i}].target`,
        message: `Edge references unknown target node: "${e.target}"`,
        severity: "error",
      })
    }
    if (e.source === e.target) {
      errors.push({
        path: `edges[${i}]`,
        message: `Self-referencing edge: "${e.source}" → "${e.target}"`,
        severity: "warning",
      })
    }
    if (edgeIds.has(e.id))
      errors.push({ path: `edges[${i}].id`, message: `Duplicate edge id: "${e.id}"`, severity: "error" })
    edgeIds.add(e.id)
  })

  const viewIds = new Set<string>()
  d.views.forEach((v, i) => {
    if (!v.id) errors.push({ path: `views[${i}].id`, message: "Missing view id", severity: "error" })
    if (!v.name) errors.push({ path: `views[${i}].name`, message: "Missing view name", severity: "error" })
    if (!v.layout || !["TB", "LR", "manual", "radial"].includes(v.layout)) {
      errors.push({
        path: `views[${i}].layout`,
        message: `Invalid layout: "${v.layout}" (must be "TB", "LR", "manual", or "radial")`,
        severity: "error",
      })
    }
    if (v.layers) {
      v.layers.forEach((lid, j) => {
        if (!layerIds.has(lid)) {
          errors.push({
            path: `views[${i}].layers[${j}]`,
            message: `View references unknown layer: "${lid}"`,
            severity: "error",
          })
        }
      })
    }
    if (v.nodes) {
      v.nodes.forEach((nodeId, j) => {
        if (!nodeIds.has(nodeId)) {
          errors.push({
            path: `views[${i}].nodes[${j}]`,
            message: `View references unknown node: "${nodeId}"`,
            severity: "error",
          })
        }
      })
    }
    if (viewIds.has(v.id))
      errors.push({ path: `views[${i}].id`, message: `Duplicate view id: "${v.id}"`, severity: "error" })
    viewIds.add(v.id)
  })

  // defaultView
  if (!d.defaultView || typeof d.defaultView !== "string") {
    errors.push({ path: "defaultView", message: "Missing defaultView", severity: "error" })
  } else if (!viewIds.has(d.defaultView)) {
    errors.push({
      path: "defaultView",
      message: `defaultView references unknown view: "${d.defaultView}"`,
      severity: "error",
    })
  }

  // Warnings
  if (d.nodes.length === 0) {
    errors.push({ path: "nodes", message: "Spec has no nodes", severity: "warning" })
  }

  // Orphan nodes (no edges connected)
  const connectedNodes = new Set<string>()
  d.edges.forEach((e) => {
    connectedNodes.add(e.source)
    connectedNodes.add(e.target)
  })
  d.nodes.forEach((n) => {
    if (!connectedNodes.has(n.id) && d.edges.length > 0) {
      errors.push({ path: `nodes(${n.id})`, message: `Orphan node: "${n.id}" has no edges`, severity: "warning" })
    }
  })

  return errors
}
